import { useState, useEffect, useCallback } from 'react';
import { getWhitelist, addWhitelistDomain, removeWhitelistDomain } from '@/services/admin';
import { useAuth } from '@/hooks/useAuth';

export const useAdminWhitelist = () => {
  const [domains, setDomains] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchDomains = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getWhitelist();
      setDomains(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch whitelist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const add = async (domain: string) => {
    if (!user) throw new Error('Not authenticated');
    try {
      await addWhitelistDomain(domain, user.uid);
      setDomains((prev) => [...prev, domain.toLowerCase().replace(/^www\./, '').trim()]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add domain';
      setError(message);
      throw err;
    }
  };

  const remove = async (domain: string) => {
    if (!user) throw new Error('Not authenticated');
    try {
      await removeWhitelistDomain(domain, user.uid);
      setDomains((prev) => prev.filter((d) => d !== domain));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove domain';
      setError(message);
      throw err;
    }
  };

  return {
    domains,
    loading,
    error,
    add,
    remove,
    refresh: fetchDomains,
  };
};
